<template>
  <AppGrid v-if="showGrid" />
  <AppTransition />
  <AppMailConfirm v-if="isMailConfirmVisible" />
  <About :data="about.data" v-if="shouldShowAboutModal" />
  <AppLoadingNew v-if="isFirstLoad" />
  <AppMenuMobile v-if="isMenuOpen" />
  <ClientOnly>
    <AppDomDistortion v-if="!$device.isMobileOrTablet && !$mq.isMobileOrTablet" />
  </ClientOnly>
  <Teleport to="body" :disabled="!isHeaderMounted">
    <AppHeader />
  </Teleport>
  <Teleport to="body" :disabled="!isHeaderMounted">
    <AppCursor />
  </Teleport>
  <div
    id="app"
    :class="{
      'is-mobile': $device.isMobile,
      'is-desktop': !$device.isMobile,
      'is-safari': $device.isSafari,
      'is-edge': $device.isEdge,
      'is-firstload': isFirstLoad,
      'is-transitioning': isTransitioning,
      'is-loaded': isLoaded,
      'has-modal': isModalVisible,
    }"
  >
    <NuxtPage />
  </div>
</template>

<script setup>
const isHeaderMounted = ref(false);
const showGrid = useShowGrid();
const isFirstLoad = useFirstLoad();
const isLoaded = useAppLoaded();
const isTransitioning = useTransitioning();
const isModalVisible = useAboutModal();
const isMailConfirmVisible = useMailConfirmModal();
const isMenuOpen = useMenuOpen();

const prismic = usePrismic();
const { data: about } = await useAsyncData('about', () =>
  prismic.client.getSingle('about')
);

const { data: resourcesData } = await useAsyncData(
  'resources',
  useFetchGlobalData
);

const router = useRouter();
const route = useRoute();
const { $Lenis } = useNuxtApp();
const siteUrl = 'https://priscofrancesco.com';
const services = [
  'Front-end Development',
  'API Integration',
  'Headless CMS',
  'Design System Architecture',
  'Animation Driven Development',
  'User Interface Design',
  'Art Direction',
  'Design System',
  'Prototyping',
];

const personJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': `${siteUrl}/#francesco-prisco`,
  name: 'Francesco Prisco',
  url: siteUrl,
  image: `${siteUrl}${meta.image}`,
  jobTitle: 'Independent Developer and Designer',
  description:
    'Francesco Prisco is an independent developer and designer based in Italy, creating motion-driven websites and digital experiences for studios, brands, and enterprise teams.',
  email: 'hello@priscofrancesco.com',
  nationality: {
    '@type': 'Country',
    name: 'Italy',
  },
  knowsAbout: [
    'Web design',
    'Front-end development',
    'Creative development',
    'Motion design',
    'Interaction design',
    'Digital experiences',
    ...services,
  ],
  makesOffer: services.map(service => ({
    '@type': 'Offer',
    itemOffered: {
      '@type': 'Service',
      name: service,
      provider: {
        '@id': `${siteUrl}/#francesco-prisco`,
      },
    },
  })),
  sameAs: [
    'https://www.awwwards.com/francescoprisco/',
    'https://www.awwwards.com/jury-member/francescoprisco',
    'https://it.linkedin.com/in/francesco-prisco',
    'https://www.instagram.com/francescophra/',
    'https://dribbble.com/francescophra',
    'https://x.com/francescophra',
    'https://www.cssdesignawards.com/doty2023/nominees/francesco-prisco',
  ],
};

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${siteUrl}/#website`,
  name: meta.title,
  url: siteUrl,
  publisher: {
    '@id': `${siteUrl}/#francesco-prisco`,
  },
};

const profilePageJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  '@id': `${siteUrl}/about#profile-page`,
  name: `About ${meta.titleShort}`,
  url: `${siteUrl}/about`,
  description: meta.description,
  mainEntity: {
    '@id': `${siteUrl}/#francesco-prisco`,
  },
}));

const jsonLdScripts = computed(() => [
  {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(personJsonLd),
  },
  {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(websiteJsonLd),
  },
  ...(route.path === '/about'
    ? [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(profilePageJsonLd.value),
        },
      ]
    : []),
]);

const shouldShowAboutModal = computed(
  () => isModalVisible.value && (route.path !== '/about' || !isFirstLoad.value)
);

useHead({
  htmlAttrs: {
    lang: 'it',
  },
  script: jsonLdScripts,
});

useSeoMeta({
  title: `${meta.title}`,
  ogTitle: meta.title,
  description: meta.description,
  ogDescription: meta.description,
  ogImage: meta.image,
  ogType: 'website',
  ogSiteName: meta.title,
  twitterCard: 'summary_large_image',
  twitterTitle: meta.title,
  twitterDescription: meta.description,
  twitterImage: meta.image,
});

watch(
  [() => route.path, () => isFirstLoad.value],
  ([path, firstLoad]) => {
    if (path === '/about') {
      isModalVisible.value = !firstLoad;
    }
  },
  { immediate: true }
);

onMounted(() => {
  isHeaderMounted.value = true;
  $Lenis.start();
});
</script>
